/**
 * Inbound DO messages, run-stream frames and the RequestTurn payload: v1 → v2 (decision 0036, spec §2.8, §2.12).
 * Old Worker isolates and queued outbox rows deliver v1 shapes for a while after the deploy.
 */
import type {
  AgentInbound,
  ProjectInbound,
  RequestTurnPayload,
  RunInbound,
  WorkspaceInbound,
} from "../internal"
import type { JobTemplate } from "../job"
import type * as V1 from "../legacy/v1/index"
import type { BodyToMind } from "../stream"
import { fromV1Command } from "./commands"
import { fromV1Placement, itemId } from "./common"
import {
  fromV1Agent,
  fromV1ContextPack,
  fromV1MemoryItem,
  fromV1Run,
  fromV1WakeBatch,
  fromV1WakeReason,
} from "./records"

type V1JobTemplate = V1.JobTemplate
type V1Workspace = typeof V1.JobWorkspace.Type

const fromV1JobWorkspace = (w: V1Workspace): JobTemplate["workspace"] => {
  switch (w._tag) {
    case "worktree":
      return { ...w, reuseOf: w.reuseOf === null ? null : itemId(w.reuseOf) }
    case "none":
    case "scratch":
      return w
    default:
      return w satisfies never
  }
}

/** D7: a v1 job template has the same JSON; only the id brands differ (`taskId` holds an item id). */
export const fromV1JobTemplate = (j: V1JobTemplate): JobTemplate => ({
  ...j,
  taskId: j.taskId === null ? null : itemId(j.taskId),
  actor:
    j.actor._tag === "worker"
      ? { _tag: "worker", taskId: itemId(j.actor.taskId), agentId: j.actor.agentId }
      : j.actor,
  workspace: fromV1JobWorkspace(j.workspace),
})

export const fromV1ProjectInbound = (m: typeof V1.ProjectInbound.Type): ProjectInbound => ({
  ...m,
  command: fromV1Command(m.command),
})

export const fromV1WorkspaceInbound = (m: typeof V1.WorkspaceInbound.Type): WorkspaceInbound => {
  switch (m._tag) {
    case "Enqueue":
      return { ...m, placement: fromV1Placement(m.placement), job: fromV1JobTemplate(m.job) }
    case "ApplyMemory":
      return { ...m, item: fromV1MemoryItem(m.item) }
    case "Cancel":
    case "ProjectIndexChanged":
      return m
    default:
      return m satisfies never
  }
}

export const fromV1AgentInbound = (m: typeof V1.AgentInbound.Type): AgentInbound => {
  switch (m._tag) {
    case "Wake":
      return { ...m, reason: fromV1WakeReason(m.reason) }
    case "PushAgentConfig":
      return { ...m, agent: fromV1Agent(m.agent) }
    case "PermissionDecision":
    case "TurnRunEnded":
      return m
    default:
      return m satisfies never
  }
}

export const fromV1RunInbound = (m: typeof V1.RunInbound.Type): RunInbound => {
  switch (m._tag) {
    case "Prepare":
      return { ...m, run: fromV1Run(m.run), job: fromV1JobTemplate(m.job) }
    case "PermissionDecision":
      return m
    default:
      return m satisfies never
  }
}

export const fromV1RequestTurnPayload = (p: {
  readonly runId: V1.RunId
  readonly agentId: V1.AgentId
  readonly wakeBatch: typeof V1.WakeBatch.Type
}): RequestTurnPayload => ({ ...p, wakeBatch: fromV1WakeBatch(p.wakeBatch) })

/** Run-stream frames body → mind: only `turn.start` carries v1 shapes (its wake batch and context pack). */
export const fromV1BodyToMind = (f: typeof V1.BodyToMind.Type): typeof BodyToMind.Type => {
  switch (f.type) {
    case "turn.start":
      return {
        ...f,
        payload: {
          ...f.payload,
          wake: f.payload.wake === null ? null : fromV1WakeBatch(f.payload.wake),
          contextPack: fromV1ContextPack(f.payload.contextPack),
        },
      }
    case "turn.steer":
    case "turn.interrupt":
    case "permission.response":
    case "ack":
    case "session.load":
    case "turn.resume":
      return f
    default:
      return f satisfies never
  }
}
